import { Order, Product } from "@/prisma-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  CheckCircle2,
  Circle,
  Clock,
  CreditCard,
  Mail,
  Package2,
  Phone,
  Plus,
  User,
} from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { samedayStatusConfig } from "../../config/sameday.status.config";
import { useIpBlacklist } from "../../context/IpBlacklistContext";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useProducts } from "../../hooks/useProducts";
import { OrderStatus, PaymentStatus } from "../../prisma-types";
import { formatDate, formatPrice } from "../../utils/format";
import BillingAddressCard from "./BillingAddressCard";
import { EditContainer } from "./EditContainer";
import OrderAddressContainer from "./OrderAddressContainer";
import OrderHeaderActions from "./OrderHeaderActions";
import { OrderObservatiiCard } from "./OrderObservatiiCard";

const paymentStatusColors: Record<PaymentStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
  REFUNDED: "bg-gray-100 text-gray-800",
};

const PAYMENT_STATUS_TRANSLATIONS: Record<PaymentStatus, string> = {
  PENDING: "În așteptare",
  PROCESSING: "În procesare",
  COMPLETED: "Finalizată",
  FAILED: "Eșuată",
  REFUNDED: "Rambursată",
};

export function OrderDetailsPage() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { add: addIp } = useIpBlacklist();
  // no-op state removed

  // Local state for adding products to the order (UI-only)
  const [isAddingProduct, setIsAddingProduct] = React.useState(false);
  const [productSearch, setProductSearch] = React.useState("");
  const [localItems, setLocalItems] = React.useState<Order["orderItems"]>([]);

  const {
    data: order,
    isLoading,
    isFetching,
  } = useQuery<Order>({
    staleTime: 60000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.awbNumber) return false;

      const statusId = data.metadata?.shippingStatusDetails?.statusId;
      if (!statusId) return false;

      const status = samedayStatusConfig.statusMapping[statusId];
      if (!status) return false;

      // Active delivery (statuses 3-6)
      if (statusId >= 3 && statusId <= 6) return 30000; // 30 seconds

      // Pending pickup (statuses 1-2)
      if (statusId <= 2) return 60000; // 1 minute

      // Problem states (statuses 8-9)
      if (statusId === 8 || statusId === 9) return 300000; // 5 minutes

      // Completed/cancelled (statuses 7,15,16,17)
      return false;
    },
    queryKey: ["order", id],
    queryFn: async () => {
      const response = await fetchWithAuth(`/api/admin/orders/${id}`, {
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Eroare la încărcarea comenzii");
      return response.json();
    },
  });

  console.log("order", order);

  // Initialize/refresh local items when order loads
  React.useEffect(() => {
    if (order?.orderItems) {
      setLocalItems(order.orderItems);
    }
  }, [order?.orderItems]);

  // Fetch products only when Add Product panel is open
  const { data: allProducts = [], isLoading: productsLoading } = useProducts({
    enabled: isAddingProduct,
  });

  const filteredProducts = React.useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    if (!term) return allProducts;
    return allProducts.filter((p) =>
      (p.name || "").toLowerCase().includes(term)
    );
  }, [allProducts, productSearch]);

  const handleAddProductToOrder = (p: Product) => {
    setLocalItems((prev) => [
      ...prev,
      {
        id: -Date.now(),
        quantity: 1,
        price: p.price || 0,
        productTitle: p.name || "",
        totalPrice: p.price || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        orderId: order?.id || 0,
      },
    ]);
  };

  const hasLocalChanges = React.useMemo(() => {
    if (!order) return false;
    return localItems.length !== order.orderItems.length;
  }, [order, localItems]);

  // Cash-on-delivery processing fee
  const isCOD = order?.payment?.method === "RAMBURS";
  const processingFee = order?.metadata?.codFee ?? (isCOD ? 3 : 0);

  // Calculate prices with promo discounts
  const promo = order?.metadata?.promo;

  // Calculate cumulative position of units based on variantIdsInOrder
  const calculateUnitPositions = React.useMemo(() => {
    if (!promo || !promo.variantIdsInOrder) return new Map<number, number[]>();

    // Map variantId to array of unit positions (0-indexed)
    const variantPositions = new Map<number, number[]>();
    let currentPosition = 0;

    promo.variantIdsInOrder.forEach((variantId) => {
      if (!variantPositions.has(variantId)) {
        variantPositions.set(variantId, []);
      }
      variantPositions.get(variantId)!.push(currentPosition);
      currentPosition++;
    });

    return variantPositions;
  }, [promo]);

  // Calculate item prices with discounts applied
  const calculateItemPrice = React.useCallback(
    (item: Order["orderItems"][0]) => {
      if (!promo || !item.productVariantId) {
        return {
          originalPrice: item.price,
          discountedPrice: item.price,
          discountAmount: 0,
          hasDiscount: false,
        };
      }

      // Get positions for this variantId
      const unitPositions = calculateUnitPositions.get(item.productVariantId);
      if (!unitPositions || unitPositions.length === 0) {
        return {
          originalPrice: item.price,
          discountedPrice: item.price,
          discountAmount: 0,
          hasDiscount: false,
        };
      }

      // Calculate which units of this item get discount
      // Discount applies starting from unit 2 (index 1) onwards, up to discountUnits total
      // Unit positions: 0, 1, 2, 3, ... (0-indexed)
      // Discount applies to units at positions: 1, 2, 3, ... (up to discountUnits)
      let discountedQuantity = 0;
      unitPositions.forEach((position) => {
        // Unit at position (0-indexed)
        // Discount applies if position >= 1 and position < 1 + discountUnits
        if (position >= 1 && position < 1 + promo.discountUnits) {
          discountedQuantity++;
        }
      });

      // Limit discounted quantity to item's quantity
      discountedQuantity = Math.min(discountedQuantity, item.quantity);

      if (discountedQuantity === 0) {
        return {
          originalPrice: item.price,
          discountedPrice: item.price,
          discountAmount: 0,
          hasDiscount: false,
        };
      }

      // Calculate discount per unit
      const discountPerUnit =
        promo.discountUnits > 0
          ? promo.discountAmount / promo.discountUnits
          : 0;

      const discountForThisItem = discountPerUnit * discountedQuantity;
      const discountedPrice = item.price - discountPerUnit;

      return {
        originalPrice: item.price,
        discountedPrice: Math.max(0, discountedPrice), // Ensure price doesn't go negative
        discountAmount: discountForThisItem,
        hasDiscount: discountForThisItem > 0,
        discountedQuantity,
      };
    },
    [promo, calculateUnitPositions]
  );

  // Calculate total with discounts
  const calculateTotalWithDiscounts = React.useCallback(() => {
    let subtotal = 0;

    localItems.forEach((item) => {
      const priceInfo = calculateItemPrice(item);
      subtotal += priceInfo.discountedPrice * item.quantity;
    });

    // Add processing fee
    subtotal += processingFee;

    // Add delivery price (0 if 3+ products)
    const deliveryPrice = order?.metadata?.deliveryPrice ?? 0;
    subtotal += deliveryPrice;

    return subtotal;
  }, [
    localItems,
    calculateItemPrice,
    processingFee,
    order?.metadata?.deliveryPrice,
  ]);

  const totalWithDiscounts = React.useMemo(() => {
    return calculateTotalWithDiscounts();
  }, [calculateTotalWithDiscounts]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-foreground mb-6">
          Comanda nu a fost găsită
        </h1>
      </div>
    );
  }

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return "N/A";
    try {
      return formatDate(timestamp);
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="p-6 space-y-8 relative bg-background min-h-screen">
      {/* Loading overlay for status updates */}
      {isFetching && order?.awbNumber && (
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-background p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
            <p className="mt-2 text-sm text-foreground">
              Actualizare status...
            </p>
          </div>
        </div>
      )}
      <OrderHeaderActions order={order} id={id as string} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Order Status */}
        <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-foreground" />
            Status Comandă
          </h2>

          {order.payment?.method === "NETOPIA" && (
            <div
              className={`${
                order.metadata?.confirmationEmailSent &&
                order.metadata?.shippingEmailSent
                  ? "bg-green-50 border-green-200"
                  : "bg-yellow-50 border-yellow-200"
              } border rounded-lg p-4 mb-4`}
            >
              <h3
                className={`font-medium flex items-center gap-2 mb-2 ${
                  order.metadata?.confirmationEmailSent &&
                  order.metadata?.shippingEmailSent
                    ? "text-green-800"
                    : "text-yellow-800"
                }`}
              >
                {order.metadata?.confirmationEmailSent &&
                order.metadata?.shippingEmailSent ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Toate Emailurile Au Fost Trimise
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-5 h-5" />
                    Atenție - Emailuri Important de Trimis
                  </>
                )}
              </h3>
              <ul
                className={`space-y-2 text-sm ${
                  order.metadata?.confirmationEmailSent &&
                  order.metadata?.shippingEmailSent
                    ? "text-green-700"
                    : "text-yellow-700"
                }`}
              >
                <li className="flex items-center gap-2">
                  {order.metadata?.confirmationEmailSent ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Circle className="w-2 h-2" fill="currentColor" />
                  )}
                  {order.metadata?.confirmationEmailSent
                    ? "Email de Confirmare Trimis"
                    : "Trimite Email de Confirmare"}
                </li>
                <li className="flex items-center gap-2">
                  {order.metadata?.shippingEmailSent ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Circle className="w-2 h-2" fill="currentColor" />
                  )}
                  {order.metadata?.shippingEmailSent
                    ? "Email Factura si AWB Trimis"
                    : "Trimite Email - Factura si AWB"}
                </li>
              </ul>
            </div>
          )}
          {order.clientIp && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-foreground">Client IP:</span>
              <span className="font-mono">{order.clientIp}</span>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-destructive border-destructive hover:bg-destructive/10"
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Blochezi IP-ul ${order.clientIp}? Această acțiune va adăuga IP-ul în blacklist.`
                    )
                  ) {
                    return;
                  }
                  try {
                    await addIp(order.clientIp as string);
                    toast.success("IP adăugat în blacklist");
                  } catch (error) {
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : "Eroare la adăugarea IP-ului în blacklist"
                    );
                  }
                }}
              >
                Blochează IP
              </Button>
            </div>
          )}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <span className="text-foreground w-20">Plată:</span>
              <Badge
                className={`px-3 py-1 text-sm font-medium ${
                  order.status === OrderStatus.CANCELLED
                    ? "bg-red-100 text-red-800"
                    : order.payment?.method === "NETOPIA" &&
                      order.payment?.status === "COMPLETED"
                    ? "bg-green-100 text-green-800"
                    : order.payment?.method === "RAMBURS" &&
                      order.metadata?.paymentStatus === "PAID"
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                } shadow-sm`}
              >
                {order.status === OrderStatus.CANCELLED
                  ? "Anulată"
                  : order.payment?.method === "NETOPIA" &&
                    order.payment?.status === "COMPLETED"
                  ? "Plătit"
                  : order.payment?.method === "RAMBURS" &&
                    order.metadata?.paymentStatus === "PAID"
                  ? "Plătit"
                  : order.payment?.method === "RAMBURS"
                  ? "Ramburs"
                  : "În așteptare"}
              </Badge>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-foreground w-20">Livrare:</span>
              <Badge
                className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                  order.status === OrderStatus.CANCELLED
                    ? "bg-red-100 text-red-800 ring-red-200"
                    : order.awbNumber
                    ? (() => {
                        const status =
                          samedayStatusConfig.statusMapping[
                            order.metadata?.shippingStatusDetails?.statusId || 1
                          ];
                        switch (status) {
                          case "DELIVERED":
                            return "bg-green-100 text-green-800";
                          case "IN_TRANSIT":
                            return "bg-yellow-100 text-yellow-800";
                          case "FAILED":
                            return "bg-red-100 text-red-800";
                          case "RETURNED":
                            return "bg-purple-100 text-purple-800";
                          case "CANCELED":
                            return "bg-red-100 text-red-800";
                          case "REGISTERED":
                          default:
                            return "bg-blue-100 text-blue-800";
                        }
                      })() + " ring-current/20"
                    : "bg-yellow-100 text-yellow-800 ring-yellow-200"
                }`}
              >
                {order.status === OrderStatus.CANCELLED
                  ? "Anulată"
                  : order.awbNumber
                  ? order.metadata?.shippingStatusDetails?.statusId
                    ? samedayStatusConfig.statusLabels[
                        order.metadata.shippingStatusDetails.statusId
                      ]
                    : "Comandă înregistrată"
                  : order.status === OrderStatus.PENDING
                  ? "În așteptare"
                  : order.status === OrderStatus.PAID
                  ? "Plătită"
                  : order.status === OrderStatus.PROCESSING
                  ? "În procesare"
                  : "În așteptare"}
              </Badge>
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2  transition-colors duration-200 whitespace-nowrap"
                onClick={async () => {
                  try {
                    const response = await fetchWithAuth(
                      `/api/admin/orders/${order.id}/generate-awb`,
                      {
                        method: "POST",
                      }
                    );
                    if (!response.ok) {
                      const errorData = await response.json();
                      throw new Error(
                        errorData.message || "Eroare la generarea AWB"
                      );
                    }
                    // Invalidate both the order list and current order details
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ["orders"] }),
                      queryClient.invalidateQueries({
                        queryKey: ["order", order.id],
                      }),
                    ]);
                    toast.success("AWB generat cu succes");
                  } catch (error) {
                    if (error instanceof Error) {
                      toast.error(error.message);
                    } else {
                      toast.error("Eroare la generarea AWB");
                    }
                  }
                }}
                disabled={!!order.awbNumber}
              >
                Generează AWB
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const response = await fetchWithAuth(
                      `/api/admin/invoices/orders/${order.id}`,
                      {
                        method: "POST",
                      }
                    );
                    if (!response.ok)
                      throw new Error("Eroare la generarea facturii");
                    // Invalidate both the order list and current order details
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ["orders"] }),
                      queryClient.invalidateQueries({
                        queryKey: ["order", order.id],
                      }),
                    ]);
                    toast.success("Factură generată cu succes");
                  } catch (error) {
                    toast.error("Eroare la generarea facturii");
                  }
                }}
                disabled={!!order.invoiceNumber}
              >
                Generează Factură
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2  whitespace-nowrap"
                onClick={async () => {
                  try {
                    // Send confirmation email
                    const emailResponse = await fetchWithAuth(
                      `/api/admin/orders/${order.id}/send-confirmation`,
                      {
                        method: "POST",
                      }
                    );
                    if (!emailResponse.ok)
                      throw new Error("Eroare la trimiterea email-ului");

                    // Update order metadata on server
                    const metadataResponse = await fetchWithAuth(
                      `/api/admin/orders/${order.id}/metadata`,
                      {
                        method: "PATCH",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          confirmationEmailSent: true,
                        }),
                      }
                    );
                    if (!metadataResponse.ok)
                      throw new Error("Eroare la actualizarea statusului");

                    // Refetch order data to get updated metadata
                    await queryClient.invalidateQueries({
                      queryKey: ["order", id],
                    });

                    toast.success("Email trimis cu succes");
                  } catch (error) {
                    toast.error("Eroare la trimiterea email-ului");
                  }
                }}
                disabled={order.metadata?.confirmationEmailSent}
              >
                <Mail className="w-4 h-4" />
                Trimite Email de Confirmare
              </Button>

              {order.awbNumber &&
                order.invoiceSeries &&
                order.invoiceNumber && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 hover:bg-gray-100 whitespace-nowrap"
                    onClick={async () => {
                      try {
                        // Send shipping email
                        const emailResponse = await fetchWithAuth(
                          `/api/admin/orders/${order.id}/send-shipping-email`,
                          {
                            method: "POST",
                          }
                        );
                        if (!emailResponse.ok)
                          throw new Error("Eroare la trimiterea email-ului");

                        // Update order metadata on server
                        const metadataResponse = await fetchWithAuth(
                          `/api/admin/orders/${order.id}/metadata`,
                          {
                            method: "PATCH",
                            headers: {
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                              shippingEmailSent: true,
                            }),
                          }
                        );
                        if (!metadataResponse.ok)
                          throw new Error("Eroare la actualizarea statusului");

                        // Refetch order data to get updated metadata
                        await queryClient.invalidateQueries({
                          queryKey: ["order", id],
                        });

                        toast.success("Email trimis cu succes");
                      } catch (error) {
                        toast.error("Eroare la trimiterea email-ului");
                      }
                    }}
                    disabled={order.metadata?.shippingEmailSent}
                  >
                    <Mail className="w-4 h-4" />
                    Trimite Email - Factura si AWB
                  </Button>
                )}
            </div>
          </div>
        </Card>

        {/* Customer Info */}
        <Card className="p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-foreground" />
            Informații Client
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                Nume:
              </span>
              <span>{order.metadata?.customerName || order.user.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email:
              </span>
              <span>{order.user.email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-foreground flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Telefon:
              </span>
              <div className="flex items-center gap-2">
                <span>{order.metadata?.customerPhone}</span>
                {order.payment?.method === "RAMBURS" &&
                  (order.metadata?.manuallyVerified ? (
                    <Badge className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full shadow-sm ring-1 ring-green-200 flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Confirmat Manual
                    </Badge>
                  ) : order.metadata?.phoneVerified ? (
                    <Badge className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full shadow-sm ring-1 ring-blue-200 flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Verificat SMS
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 px-3 py-1.5 rounded-full shadow-sm ring-1 ring-red-200 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Necesită Verificare
                    </Badge>
                  ))}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data comenzii:
              </span>
              <span>{formatDate(order.createdAt)}</span>
            </div>
            {order.payment?.method === "RAMBURS" &&
              !order.awbNumber &&
              !order.metadata?.manuallyVerified &&
              !order.metadata?.phoneVerified && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      if (
                        window.confirm(
                          "Confirmați că ați verificat telefonic clientul?"
                        )
                      ) {
                        try {
                          const response = await fetchWithAuth(
                            `/api/admin/orders/${order.id}/verify-phone/manual`,
                            {
                              method: "POST",
                            }
                          );
                          if (!response.ok)
                            throw new Error(
                              "Eroare la verificarea telefonului"
                            );

                          await queryClient.invalidateQueries({
                            queryKey: ["order", order.id],
                          });
                          toast.success("Telefon verificat cu succes");
                        } catch (error) {
                          toast.error("Eroare la verificarea telefonului");
                        }
                      }
                    }}
                  >
                    Confirmă Verificare Telefonică
                  </Button>
                </div>
              )}
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <OrderAddressContainer order={order} />

          <EditContainer order={order} />
        </div>

        <div className="flex flex-col gap-4">
          <BillingAddressCard order={order} />
          {/* Shipping Status & History */}
          <Card className="p-4">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Package2 className="w-5 h-5 text-foreground" />
              Status Livrare
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-foreground">Status:</span>
                <Badge
                  className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                    order.status === OrderStatus.CANCELLED
                      ? "bg-red-100 text-red-800 ring-red-200"
                      : order.awbNumber
                      ? (() => {
                          const status =
                            samedayStatusConfig.statusMapping[
                              order.metadata?.shippingStatusDetails?.statusId ||
                                1
                            ];
                          switch (status) {
                            case "DELIVERED":
                              return "bg-green-100 text-green-800";
                            case "IN_TRANSIT":
                              return "bg-yellow-100 text-yellow-800";
                            case "FAILED":
                              return "bg-red-100 text-red-800";
                            case "RETURNED":
                              return "bg-purple-100 text-purple-800";
                            case "CANCELED":
                              return "bg-red-100 text-red-800";
                            case "REGISTERED":
                            default:
                              return "bg-blue-100 text-blue-800";
                          }
                        })() + " ring-current/20"
                      : "bg-yellow-100 text-yellow-800 ring-yellow-200"
                  }`}
                >
                  {order.status === OrderStatus.CANCELLED
                    ? "Anulată"
                    : order.awbNumber
                    ? order.metadata?.shippingStatusDetails?.statusId
                      ? samedayStatusConfig.statusLabels[
                          order.metadata.shippingStatusDetails.statusId
                        ]
                      : "Comandă înregistrată"
                    : order.status === OrderStatus.PENDING
                    ? "În așteptare"
                    : order.status === OrderStatus.PAID
                    ? "Plătită"
                    : order.status === OrderStatus.PROCESSING
                    ? "În procesare"
                    : "În așteptare"}
                </Badge>
              </div>

              {order.awbNumber && order.metadata?.shippingStatusDetails && (
                <div className="mt-4 space-y-4">
                  <div className="bg-background p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="space-y-1">
                      <div>
                        <span className="text-foreground">Stare:</span>{" "}
                        {order.metadata.shippingStatusDetails.statusState}
                      </div>
                      {order.metadata.shippingStatusDetails.transitLocation && (
                        <div>
                          <span className="text-foreground">Locație:</span>{" "}
                          {order.metadata.shippingStatusDetails.transitLocation}
                        </div>
                      )}
                      {order.metadata.shippingStatusDetails.reason && (
                        <div>
                          <span className="text-foreground">Motiv:</span>{" "}
                          {order.metadata.shippingStatusDetails.reason}
                        </div>
                      )}
                      {order.metadata.shippingStatusDetails.deliveryAttempts &&
                        order.metadata.shippingStatusDetails.deliveryAttempts >
                          0 && (
                          <div>
                            <span className="text-foreground">
                              Încercări livrare:
                            </span>{" "}
                            {
                              order.metadata.shippingStatusDetails
                                .deliveryAttempts
                            }
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {(order.metadata?.trackingHistory || []).length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium">Istoric livrare:</h3>
                  <div className="space-y-2">
                    {((order.metadata?.trackingHistory || []) as any[]).map(
                      (entry, index) => (
                        <div
                          key={index}
                          className="bg-background p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <Badge
                              className={(() => {
                                const status =
                                  samedayStatusConfig.statusMapping[
                                    entry.samedayStatus
                                  ];
                                switch (status) {
                                  case "DELIVERED":
                                    return "bg-green-100 text-green-800";
                                  case "IN_TRANSIT":
                                    return "bg-yellow-100 text-yellow-800";
                                  case "FAILED":
                                    return "bg-red-100 text-red-800";
                                  case "RETURNED":
                                    return "bg-purple-100 text-purple-800";
                                  case "CANCELED":
                                    return "bg-red-100 text-red-800";
                                  case "REGISTERED":
                                  default:
                                    return "bg-blue-100 text-blue-800";
                                }
                              })()}
                            >
                              {
                                samedayStatusConfig.statusLabels[
                                  entry.samedayStatus
                                ]
                              }
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {formatTimestamp(entry.timestamp)}
                            </span>
                          </div>
                          <div className="space-y-1">
                            <div className="text-sm text-foreground">
                              {entry.samedayState}
                            </div>
                            {entry.transitLocation && (
                              <div className="text-sm text-foreground">
                                Locație: {entry.transitLocation}
                              </div>
                            )}
                            {entry.reason && (
                              <div className="text-sm text-foreground">
                                Motiv: {entry.reason}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Payment Info */}
        <Card className="p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-foreground" />
            Informații Plată
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-foreground flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Metodă:
              </span>
              <span>
                {order.payment?.method === "NETOPIA" ? "Card" : "Ramburs"}
              </span>
            </div>
            {order.metadata?.invoice?.documentId && (
              <div className="flex justify-between">
                <span className="text-foreground">Document ID:</span>
                <span className="font-mono">
                  {order.metadata.invoice.documentId}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-foreground">Status:</span>
              <Badge
                className={
                  order.status === OrderStatus.CANCELLED
                    ? "bg-red-100 text-red-800"
                    : paymentStatusColors[order.payment?.status || "PENDING"]
                }
              >
                {order.status === OrderStatus.CANCELLED
                  ? "Anulată"
                  : PAYMENT_STATUS_TRANSLATIONS[
                      order.payment?.status || "PENDING"
                    ]}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground">Total:</span>
              <span className="font-semibold">{order.totalPrice} RON</span>
            </div>
          </div>
        </Card>

        <OrderObservatiiCard observatii={order.metadata?.observatii || ""} />
      </div>

      {/* Order Items */}
      <Card className="p-4">
        <div className="flex justify-between items-center">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Package2 className="w-5 h-5 text-foreground" />
            Produse Comandate
          </h2>
          {!isAddingProduct && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setIsAddingProduct((v) => !v)}
            >
              <Plus className="w-4 h-4" />
              Adauga produs
            </Button>
          )}
        </div>
        {isAddingProduct && (
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <input
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="Caută produs după denumire..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
              <Button
                variant="outline"
                onClick={() => setIsAddingProduct(false)}
              >
                Închide
              </Button>
            </div>
            <div className="mt-2 max-h-64 overflow-auto border rounded-md bg-white">
              {productsLoading ? (
                <div className="p-3 text-sm text-muted-foreground">
                  Se încarcă produse...
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">
                  Nu s-au găsit produse
                </div>
              ) : (
                filteredProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between"
                    onClick={() => handleAddProductToOrder(p)}
                  >
                    <span className="truncate pr-2">{p.name}</span>
                    <span className="text-sm text-foreground">
                      {formatPrice(p.price)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
        <div className="space-y-4">
          {localItems.map((item) => {
            const priceInfo = calculateItemPrice(item);
            const itemTotal = priceInfo.discountedPrice * item.quantity;
            const originalItemTotal = priceInfo.originalPrice * item.quantity;

            return (
              <div
                key={item.id}
                className="flex justify-between items-center py-4 border-b border-gray-100 last:border-0 hover:bg-accent hover:text-accent-foreground transition-colors duration-200 px-2 rounded"
              >
                <div className="flex-1">
                  <div className="font-medium">{item.productTitle}</div>
                  <div className="text-sm text-muted-foreground">
                    {priceInfo.hasDiscount ? (
                      <>
                        <span className="line-through text-gray-400">
                          {formatPrice(priceInfo.originalPrice)}
                        </span>{" "}
                        <span className="text-green-600 font-medium">
                          {formatPrice(priceInfo.discountedPrice)}
                        </span>{" "}
                        x {item.quantity}
                      </>
                    ) : (
                      <>
                        {formatPrice(priceInfo.originalPrice)} x {item.quantity}
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {priceInfo.hasDiscount ? (
                    <div>
                      <div className="font-semibold text-green-600">
                        {formatPrice(itemTotal)}
                      </div>
                      <div className="text-xs text-muted-foreground line-through">
                        {formatPrice(originalItemTotal)}
                      </div>
                    </div>
                  ) : (
                    <div className="font-semibold">
                      {formatPrice(itemTotal)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {promo && promo.discountAmount > 0 && (
            <div className="flex justify-between items-center text-sm text-green-600 font-medium">
              <span>Reducere promo</span>
              <span>-{formatPrice(promo.discountAmount)}</span>
            </div>
          )}
          {isCOD && processingFee > 0 && (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>Taxă procesare ramburs</span>
              <span>{formatPrice(processingFee)}</span>
            </div>
          )}
          {order.metadata?.deliveryPrice !== undefined && (
            <div className="flex justify-between items-center text-sm text-muted-foreground">
              <span>Taxă livrare</span>
              <span>
                {order.metadata.deliveryPrice === 0 ? (
                  <span className="text-green-600">Gratuit</span>
                ) : (
                  formatPrice(order.metadata.deliveryPrice)
                )}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center pt-6 font-semibold text-lg border-t border-border mt-4">
            <span>Total</span>
            <span>{formatPrice(totalWithDiscounts)}</span>
          </div>
          {hasLocalChanges && (
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  alert("Changes saved (demo)");
                }}
              >
                Save changes
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
